import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { Wheel } from 'spin-wheel';
import { OutcomeConfig } from 'Rewards/services/endpoints/GetRewardsConfig/types';
import {
  buildSegments,
  easeInOutCubic,
  SPIN_DURATION_MS,
  SPIN_REVOLUTIONS,
} from '../../wheelUtils';
import styles from './WheelCanvas.module.css';

export type WheelCanvasHandle = {
  spinToItem: (segmentIndex: number) => void;
};

type WheelCanvasProps = {
  outcomes: OutcomeConfig[];
  onRest: () => void;
};

/**
 * Thin React wrapper around the spin-wheel library.
 *
 * The wheel is created imperatively inside a useEffect and exposed via a ref
 * handle so callers can trigger spins without tearing down the canvas.
 *
 * All segments share equal weight (weight: 1) — the wheel geometry is purely
 * cosmetic. The authoritative outcome is determined server-side and stored in
 * the parent's pendingResult before spinToItem is called.
 */
export const WheelCanvas = forwardRef<WheelCanvasHandle, WheelCanvasProps>(
  ({ outcomes, onRest }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const wheelRef = useRef<Wheel | null>(null);

    // Keep the onRest callback current without recreating the wheel on every
    // render — the wheel's own onRest closure reads from this ref.
    const onRestRef = useRef(onRest);
    useEffect(() => {
      onRestRef.current = onRest;
    });

    useEffect(() => {
      if (!containerRef.current || outcomes.length === 0) return;

      wheelRef.current?.remove();

      const segments = buildSegments(outcomes);

      wheelRef.current = new Wheel(containerRef.current, {
        radius: 1,
        pointerAngle: 0,
        isInteractive: false,
        pixelRatio: 0,
        itemLabelRadius: 0.82,
        itemLabelRadiusMax: 0.2,
        itemLabelAlign: 'right',
        itemLabelRotation: 0,
        itemLabelFont: 'sans-serif',
        itemLabelFontSizeMax: 500,
        itemLabelStrokeColor: 'rgba(0,0,0,0.4)',
        itemLabelStrokeWidth: 3,
        items: segments.map(({ prizeIndex }) => ({
          label: outcomes[prizeIndex].display.label,
          weight: 1,
          backgroundColor: outcomes[prizeIndex].display.color,
          labelColor: outcomes[prizeIndex].display.textColor,
        })),
        onRest: () => onRestRef.current(),
      });

      return () => {
        wheelRef.current?.remove();
        wheelRef.current = null;
      };
    }, [outcomes]);

    useImperativeHandle(ref, () => ({
      spinToItem: (segmentIndex: number) => {
        wheelRef.current?.spinToItem(
          segmentIndex,
          SPIN_DURATION_MS,
          true,
          SPIN_REVOLUTIONS,
          1,
          easeInOutCubic
        );
      },
    }));

    return (
      <div
        ref={containerRef}
        role="img"
        className={styles.canvas}
        aria-label="Prize wheel"
        data-testid="wheel-canvas"
      >
        <span className={styles.hub} aria-hidden="true">
          cc
        </span>
      </div>
    );
  }
);

WheelCanvas.displayName = 'WheelCanvas';
